import { create } from 'apisauce'
import config from '../config';
type WidgetId = number;

interface ISmartCaptchaParams {
  sitekey: string;
  callback?: (token: string) => void;
  hl?: 'ru' | 'en' | 'be' | 'kk' | 'tt' | 'uk' | 'uz' | 'tr';
  test?: boolean;
  webview?: boolean;
  invisible?: boolean;
  shieldPosition?: `top-left` | `center-left` | `bottom-left` | `top-right` | `center-right` | `bottom-right`;
  hideShield?: boolean;
}

type ISubscribeEvent = 
  | 'challenge-visible' 
  | 'challenge-hidden' 
  | 'network-error' 
  | 'javascript-error' 
  | 'success' 
  | 'token-expired';

type IUnsubscribeFunction = () => void;

interface ISmartCaptcha {
  render: (container: string | HTMLElement, params: ISmartCaptchaParams) => WidgetId;
  getResponse: (widgetId?: WidgetId) => string;
  execute: (widgetId?: WidgetId) => void;
  reset: (widgetId?: WidgetId) => void;
  destroy: (widgetId?: WidgetId) => void;
  subscribe: (widgetId: WidgetId, event: ISubscribeEvent, callback: (...args: any[]) => any) => IUnsubscribeFunction;
}

declare const smartCaptcha: ISmartCaptcha;

const container = (id: string) => ({
  show: () => {
    const div = document.getElementById(id)
    if (div) {
      div.style.display = 'flex';
    }
  },
  hide: () => {
    const div = document.getElementById(id)
    if (div) {
      div.style.display = 'none';
    }
  },
})

const api = create({
  baseURL: config.api.baseURL,
});
const sitekeyDefer: {
  promise?: Promise<string>
  resolve?: (sitekey?: string | null) => any
  reject?: (reason?: any) => any
} = {}
const fetchSitekey = async () => {
  if (sitekeyDefer.promise) {
    return sitekeyDefer.promise
  }
  sitekeyDefer.promise = new Promise((resolve, reject) => {
    sitekeyDefer.resolve = resolve as any
    sitekeyDefer.reject = reject
  })
  const res = await api.get('get_captcha_settings')
  if (!res.ok) {
    sitekeyDefer.resolve?.(null)
    try {
      return null
    } finally {
      delete sitekeyDefer.promise
    }
  }
  const data: any = res.data;
  const sitekey = data?.type === 'smart-captcha' ? data.sitekey : null;
  sitekeyDefer.resolve?.(sitekey)
  if (!sitekey) {
    delete sitekeyDefer.promise
  }
  return sitekey;
}
let currentWidgetId: number | undefined
const containerId = 'smart-captcha-container';
const captchaId = 'smart-captcha';
export const smart_captcha = {
  ready: async () => {
    // @ts-ignore
    await window.smartCaptchaReady()
    console.log('READY SMART-CAPTCHA')
  },
  show: async () => {
    // @ts-ignore
    await window.smartCaptchaReady()
    try {
      const sitekey = await fetchSitekey()
      if (typeof currentWidgetId === 'number') {
        smartCaptcha.destroy(currentWidgetId)
      }
      const widgetId = smartCaptcha.render(captchaId, {
        sitekey,
        invisible: true,
        hl: 'ru',
      })
      currentWidgetId = widgetId
      container(containerId).show()
    } catch (e) {
      console.log(e)
    }
  },
  destroy: (widgetId?: number) => {
    if (typeof widgetId !== 'number') {
      return
    }
    smartCaptcha.destroy(widgetId)
    if (widgetId === currentWidgetId) {
      currentWidgetId = undefined
    }
  },
  hide: async () => {
    smart_captcha.destroy(currentWidgetId)
    container(containerId).hide()
  },
  execute: async (...args: any[]) => {
    const defer = {} as any
    const promise = new Promise<{ token3: string }>((resolve, reject) => {
      defer.resolve = resolve
      defer.reject = reject
    })
    const div = container(containerId)
    div.show()
    if (typeof currentWidgetId === 'number') {
      smartCaptcha.destroy(currentWidgetId)
    }
    const sitekey = await fetchSitekey()
    const widgetId = smartCaptcha.render(captchaId, {
      sitekey,
      invisible: true,
      hl: 'ru',
      callback: (token) => defer.resolve({ token3: token }),
    })
    currentWidgetId = widgetId
    smartCaptcha.subscribe(widgetId, 'javascript-error', (error) => defer.reject(error))
    smartCaptcha.execute(widgetId) 
    try {
      const res = await promise
      return res
    } finally {
      div.hide()
      smart_captcha.destroy(widgetId)
    }
  },
  render: async () => {
    const div = container(containerId)
    div.show()
    const defer = {} as any
    const promise = new Promise<{ token2: string }>((resolve, reject) => {
      defer.resolve = resolve
      defer.reject = reject
    })
    if (typeof currentWidgetId === 'number') {
      smartCaptcha.destroy(currentWidgetId)
    }
    const sitekey = await fetchSitekey()
    const widgetId = smartCaptcha.render(captchaId, {
      sitekey,
      hl: 'ru',
      invisible: false,
      callback: (token) => defer.resolve({ token2: token }),
    })
    currentWidgetId = widgetId
    smartCaptcha.subscribe(widgetId, 'javascript-error', (error) => defer.reject(error))
     try {
      const res = await promise
      return res
    } finally {
      div.hide()
      smart_captcha.destroy(widgetId)
    }
  },
}
